// Settings-04 — Add Provider Key modal.
//
// Renders the BYOK Add-key flow. Loads the credential-field schema
// from GET /api/v1/agents/provider-keys/credential-shapes so the form
// fields per (provider, gateway) come straight from the backend
// registry — no parallel schema to maintain in TypeScript.
//
// Submits to POST /api/v1/agents/provider-keys/. The server is the
// source of truth for shape validation + the live credential check:
//
//   • 400 — shape error; the message stays visible inside the modal.
//   • 201 + validation_status="invalid" — the credential reached the
//     persistence layer but the live validator rejected it. We
//     surface the redacted ``validation_message`` as a yellow warning
//     so the broker knows the row exists but is inactive.
//   • 201 + validation_status="valid" — happy path; close the modal,
//     refetch the key list (handled by the mutation's onSuccess).

import { useEffect, useMemo, useState } from "react";

import { Button, Modal, Select } from "@/design-system";

import {
  useCreateProviderKeyMutation,
  useCredentialShapesQuery,
} from "./hooks";
import type {
  CreateProviderKeyResponse,
  CredentialShape,
  ProviderKeyGateway,
  ProviderKeyProvider,
} from "./types";


function pairKey(s: { provider: string; gateway: string }): string {
  return `${s.provider}:${s.gateway}`;
}


function parsePairKey(
  value: string,
): { provider: ProviderKeyProvider; gateway: ProviderKeyGateway } | null {
  const [p, g] = value.split(":");
  if (!p || !g) return null;
  return {
    provider: p as ProviderKeyProvider,
    gateway: g as ProviderKeyGateway,
  };
}


export interface AddProviderKeyModalProps {
  open: boolean;
  onClose: () => void;
}


export function AddProviderKeyModal({ open, onClose }: AddProviderKeyModalProps) {
  const shapesQ = useCredentialShapesQuery();
  const createMut = useCreateProviderKeyMutation();

  // Memoize the shapes reference — ``shapesQ.data`` returns a new
  // array on every render but its content is stable across renders.
  const shapes = useMemo(
    () => shapesQ.data?.shapes ?? [],
    [shapesQ.data],
  );
  const [pair, setPair] = useState<string>("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Reset whenever the modal re-opens (we depend only on ``open``;
  // the ``shapes`` ref is stable so we read it inside the effect).
  useEffect(() => {
    if (!open) return;
    setDisplayName("");
    setIsDefault(false);
    setFieldValues({});
    setError(null);
    setWarning(null);
    setPair((prev) => (prev ? prev : (shapes[0] ? pairKey(shapes[0]) : "")));
    // Intentionally omit ``shapes`` — we only want to reset on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When shapes arrive after the modal was opened, default to the
  // first pair if we still don't have one selected.
  useEffect(() => {
    if (!open || pair || shapes.length === 0) return;
    setPair(pairKey(shapes[0]));
  }, [open, pair, shapes]);

  const activeShape: CredentialShape | undefined = useMemo(() => {
    return shapes.find((s) => pairKey(s) === pair);
  }, [shapes, pair]);

  const onPairChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPair(e.target.value);
    // Wipe any partially-filled credential dict from the previous pair
    // so we never POST stale fields the new pair doesn't recognize.
    setFieldValues({});
    setError(null);
    setWarning(null);
  };

  const updateField = (name: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit =
    !!activeShape
    && displayName.trim().length > 0
    && activeShape.fields
      .filter((f) => f.required)
      .every((f) => (fieldValues[f.name] ?? "").trim().length > 0)
    && !createMut.isPending;

  const onSubmit = () => {
    if (!activeShape) return;
    const parsed = parsePairKey(pair);
    if (!parsed) return;
    setError(null);
    setWarning(null);

    // Only forward fields declared in the active shape.
    const credentials: Record<string, string> = {};
    for (const field of activeShape.fields) {
      const v = fieldValues[field.name];
      if (v === undefined) continue;
      if (!field.required && v.trim() === "") continue;
      credentials[field.name] = v;
    }

    createMut.mutate(
      {
        provider: parsed.provider,
        gateway: parsed.gateway,
        display_name: displayName.trim(),
        credentials,
        is_default: isDefault,
      },
      {
        onSuccess: (row: CreateProviderKeyResponse) => {
          if (row.validation_status === "invalid") {
            // Persisted-but-rejected: keep the modal open so the
            // broker reads the warning, but the row is already in
            // the list (the mutation invalidated the query).
            setWarning(
              row.validation_message
              || "The provider rejected this credential.",
            );
            return;
          }
          onClose();
        },
        onError: (err) => {
          setError(err.message || "Could not create provider key.");
        },
      },
    );
  };

  const isLoading = shapesQ.isLoading || shapesQ.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Add provider key"
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={onClose}
            disabled={createMut.isPending}
            data-testid="add-provider-key-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            loading={createMut.isPending}
            disabled={!canSubmit}
            data-testid="add-provider-key-submit"
          >
            Add key
          </Button>
        </>
      }
    >
      <div
        data-testid="add-provider-key-modal"
        className="flex flex-col gap-4"
      >
        {error && (
          <div
            role="alert"
            data-testid="add-provider-key-error"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
          >
            {error}
          </div>
        )}
        {warning && (
          <div
            role="status"
            data-testid="add-provider-key-warning"
            className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200"
          >
            {warning}
          </div>
        )}

        {isLoading && (
          <div
            data-testid="add-provider-key-loading"
            className="text-sm text-text-secondary"
          >
            Loading credential schema…
          </div>
        )}

        {!isLoading && (
          <>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="add-provider-key-pair"
                className="text-sm font-medium text-text-primary"
              >
                Provider
              </label>
              <Select
                id="add-provider-key-pair"
                data-testid="add-provider-key-pair"
                value={pair}
                onChange={onPairChange}
              >
                {shapes.map((s) => (
                  <option key={pairKey(s)} value={pairKey(s)}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="add-provider-key-display-name"
                className="text-sm font-medium text-text-primary"
              >
                Display name
              </label>
              <input
                id="add-provider-key-display-name"
                data-testid="add-provider-key-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Production Anthropic key"
                className="w-full rounded-md border border-border-subtle bg-canvas px-2 py-1.5 text-sm text-text-primary"
              />
            </div>

            {activeShape && activeShape.fields.map((field) => (
              <div key={field.name} className="flex flex-col gap-1">
                <label
                  htmlFor={`add-provider-key-field-${field.name}`}
                  className="text-sm font-medium text-text-primary"
                >
                  {field.label}
                  {!field.required && (
                    <span className="ml-1 text-xs text-text-tertiary">
                      (optional)
                    </span>
                  )}
                </label>
                <input
                  id={`add-provider-key-field-${field.name}`}
                  data-testid={`add-provider-key-field-${field.name}`}
                  type={field.type}
                  value={fieldValues[field.name] ?? ""}
                  onChange={(e) => updateField(field.name, e.target.value)}
                  autoComplete="off"
                  className="w-full rounded-md border border-border-subtle bg-canvas px-2 py-1.5 text-sm text-text-primary"
                />
              </div>
            ))}

            <label className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                data-testid="add-provider-key-is-default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span>Make this the default for its provider</span>
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}
